import { FormStep } from '@sec-loans-ui/loan-offer/steps/FormStep'
import {
  ENDPOINT_GET_LOAN_OFFER_DOCUMENT,
  LoanOfferDocumentType,
  LoanOfferMode,
  LoanOfferReason,
} from '@shared/sec-loans'
import { CommonMimeType } from '@shared/types'
import { downloadAndOpenBase64 } from '@ui-common/api/file/fileDownload'
import { useWizardForm } from '@ui-common/hooks'
import { callEndpoint, debugLog } from '@ui-common/utils'
import { Link } from '@ui-components/actions/Link'
import { BulletIcon } from '@ui-components/assets/Icons'
import { Notification } from '@ui-components/containers/Notification'
import { Divider } from '@ui-components/decorative/Divider'
import { IndentationText } from '@ui-components/decorative/IndentationText'
import { LoadingSpinner } from '@ui-components/decorative/LoadingSpinner'
import { StepSummaryView } from '@ui-components/form/StepSummaryView'
import { FlexColumnContainer, FlexRowContainer } from '@ui-components/global/CommonStyling'
import { SecondaryHeading } from '@ui-components/typography/headings/SecondaryHeading'
import { AdditionalText, LabelText } from '@ui-components/typography/Typography'
import { useState } from 'react'
import { Fragment } from 'react/jsx-runtime'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { ReservationFooter } from '../../components/ReservationFooter'
import { useExistingLoanOfferContext } from '../LoanOfferContext'
import { getOfferSummaryData } from './offerStepUtils'

const AdditionalContent = styled.div`
  padding: 1rem 0 0 1rem;
`

const LinkWrapper = styled(FlexRowContainer)`
  padding-bottom: 1rem;
  > span:nth-child(odd) {
    margin-right: 1rem;
    transform: translateY(7px);
  }
`

export const OfferStep = () => {
  const intl = useIntl()
  const { proceed, loanOffer, user } = useExistingLoanOfferContext()
  const methods = useWizardForm()

  const offerSummaryData = loanOffer.offeredLoans.map((offerReason: LoanOfferReason) =>
    getOfferSummaryData(user, offerReason, loanOffer, intl),
  )

  const isAutomaticOffer = loanOffer.loanOfferMode === LoanOfferMode.Automatic
  const notificationText = isAutomaticOffer
    ? intl.formatMessage({ id: 'loan-offer-automatic-offer-description' })
    : intl.formatMessage({ id: 'loan-offer-manual-offer-description' }, { bankerName: loanOffer.banker?.name })

  const [isLoadingEsisDocument, setIsLoadingEsisDocument] = useState(false)
  const [isLoadingAmortizationDocument, setIsLoadingAmortizatioDocument] = useState(false)

  const getFilename = (): string => {
    // filename format: "20251117_144119280_document.pdf"
    const dateAndTime = new Date()
      .toISOString()
      .replace(/\-|:|\.|Z/g, '')
      .replace('T', '_')
    return `${dateAndTime}_document.pdf`
  }

  const manageLoading: Record<LoanOfferDocumentType, (loading: boolean) => unknown> = {
    [LoanOfferDocumentType.Esis]: setIsLoadingEsisDocument,
    [LoanOfferDocumentType.AmortizationTable]: setIsLoadingAmortizatioDocument,
  }

  const downloadDocument = async (documentType: LoanOfferDocumentType, offeredLoanId: string) => {
    try {
      manageLoading[documentType](true)
      debugLog(`Download document ${documentType}`, ENDPOINT_GET_LOAN_OFFER_DOCUMENT, offeredLoanId)
      const base64EncodedPdf = await callEndpoint(ENDPOINT_GET_LOAN_OFFER_DOCUMENT, {
        queryParams: { offeredLoanId, documentType },
      })
      downloadAndOpenBase64({
        base64Encoded: base64EncodedPdf,
        documentInformation: {
          mimeType: CommonMimeType.Pdf,
          fileName: getFilename(),
        },
      })
    } catch (error) {
      console.error('Error while downloading document', error)
    } finally {
      manageLoading[documentType](false)
    }
  }

  return (
    <FormStep
      allowBack={false}
      subHeader={intl.formatMessage({ id: 'loan-offer-offer-step-heading' })}
      formMethods={methods}
      onSubmit={proceed}
      customFooter={
        <ReservationFooter loanOfferMode={loanOffer.loanOfferMode} reservationLink={loanOffer.reservationLink} />
      }
    >
      <Notification header={notificationText} status={'info'} />
      {offerSummaryData.map((item) => (
        <Fragment key={item.title}>
          <Divider />
          <SecondaryHeading header={item.title} headerCaption={item.purpose} />
          {item.coDebtorName && (
            <IndentationText>
              <FlexColumnContainer>
                <LabelText>{intl.formatMessage({ id: 'loan-offer-secondary-applicant-label' })}</LabelText>
                <AdditionalText>{item.coDebtorName}</AdditionalText>
              </FlexColumnContainer>
            </IndentationText>
          )}
          {item.targetData.length > 0 && (
            <StepSummaryView
              heading={intl.formatMessage({ id: 'sec-loans-phases-loan-target-phase-header' })}
              data={item.targetData}
              isLabelUnique={true}
              twoColumnLayoutSupport={true}
            />
          )}
          <StepSummaryView
            heading={intl.formatMessage({ id: 'loan-offer-offer-basic-information-label' })}
            data={item.offerData}
            isLabelUnique={true}
            twoColumnLayoutSupport={true}
          />
          <FlexColumnContainer>
            <SecondaryHeading header={intl.formatMessage({ id: 'loan-offer-offer-additional-information-label' })} />
            <AdditionalContent>
              {item.shouldShowEsisLink && (
                <LinkWrapper>
                  <BulletIcon />
                  <Link
                    standalone={isLoadingEsisDocument}
                    external={!isLoadingEsisDocument}
                    iconPos="after"
                    icon={
                      isLoadingEsisDocument ? (
                        <LoadingSpinner size="small" alt={intl.formatMessage({ id: 'common-wait-description' })} />
                      ) : undefined
                    }
                    onClick={
                      isLoadingEsisDocument
                        ? undefined
                        : () => downloadDocument(LoanOfferDocumentType.Esis, item.offeredLoanId)
                    }
                    disabled={isLoadingEsisDocument}
                  >
                    {intl.formatMessage({ id: 'loan-offer-offer-esis-link-text' })}
                  </Link>
                </LinkWrapper>
              )}
              <LinkWrapper>
                <BulletIcon />
                <Link
                  standalone={isLoadingAmortizationDocument}
                  external={!isLoadingAmortizationDocument}
                  iconPos="after"
                  icon={
                    isLoadingAmortizationDocument ? (
                      <LoadingSpinner size="small" alt={intl.formatMessage({ id: 'common-wait-description' })} />
                    ) : undefined
                  }
                  onClick={
                    isLoadingAmortizationDocument
                      ? undefined
                      : () => downloadDocument(LoanOfferDocumentType.AmortizationTable, item.offeredLoanId)
                  }
                  disabled={isLoadingAmortizationDocument}
                >
                  {intl.formatMessage({ id: 'loan-offer-offer-amortization-table-link-text' })}
                </Link>
              </LinkWrapper>
            </AdditionalContent>
          </FlexColumnContainer>
        </Fragment>
      ))}
    </FormStep>
  )
}
